import * as Yup from 'yup';

/**
 * NIST SP 800-63B aligned password policy (BreachLock pentest finding C.1.5).
 *
 * The server (pom-2-api `validatePassword`) is the source of truth and runs the
 * HaveIBeenPwned breach check. These client-side rules are a UX hint only —
 * length + printable-ASCII so the form gives immediate feedback. Deliberately
 * NO forced composition rules (uppercase/number/special), matching the server.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 64;

export const PASSWORD_LENGTH_MESSAGE = `Must be at least ${PASSWORD_MIN_LENGTH} characters`;
export const PASSWORD_MAX_MESSAGE = `Must be at most ${PASSWORD_MAX_LENGTH} characters`;
export const PASSWORD_RANGE_MESSAGE = `Must be ${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters`;

// Length-only regex used for OPTIONAL fields so an empty value (blank = keep
// existing password) skips validation, while a non-empty value is range-checked.
const PASSWORD_LENGTH_REGEX = new RegExp(`^.{${PASSWORD_MIN_LENGTH},${PASSWORD_MAX_LENGTH}}$`);

// Printable ASCII: space (0x20) through tilde (0x7E). Mirrors the server's
// PRINTABLE_ASCII charset rule so the requirements checklist can give live,
// in-browser feedback before the form is submitted.
export const PRINTABLE_ASCII_REGEX = /^[\x20-\x7E]+$/;

/**
 * A single password requirement surfaced to the user as a live checklist item.
 *
 * `isMet` evaluates the rule entirely client-side. The HaveIBeenPwned breach
 * check cannot run in the browser (it would leak the password), so that rule is
 * flagged `serverOnly` and the UI shows it as informational — verified by the
 * server (`validatePassword`) on submit.
 */
export interface PasswordRequirement {
  key: string;
  label: string;
  isMet: (password: string) => boolean;
  serverOnly?: boolean;
}

/**
 * The user-facing requirements list. Order matches the server's check order so
 * the checklist reads the same way an API rejection would. Kept here, next to
 * the Yup rules, so the policy lives in exactly one place on the client.
 */
export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    key: 'length',
    label: `Be ${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters long`,
    isMet: (password) =>
      password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
  },
  {
    key: 'charset',
    label: 'Use only standard letters, numbers, spaces or symbols',
    isMet: (password) => PRINTABLE_ASCII_REGEX.test(password),
  },
  {
    key: 'breach',
    label: 'Not be a common or easily guessed password',
    // Verified server-side via HaveIBeenPwned on submit — never checkable here.
    isMet: () => false,
    serverOnly: true,
  },
];

/**
 * A required password field enforcing the client-side portion of the policy.
 * Use `.oneOf` separately for confirm-password matching.
 */
export const passwordField = (requiredMessage = 'Please enter your password') =>
  Yup.string()
    .min(PASSWORD_MIN_LENGTH, PASSWORD_LENGTH_MESSAGE)
    .max(PASSWORD_MAX_LENGTH, PASSWORD_MAX_MESSAGE)
    .required(requiredMessage);

/**
 * An optional password field (e.g. admin edit where leaving it blank keeps the
 * existing password). Enforces the length range ONLY when a value is present —
 * an empty string is skipped via `excludeEmptyString`. (Chaining `.min()` after
 * `.optional()` would wrongly reject the empty string, since Yup `.optional()`
 * only permits `undefined`, not `''`.)
 */
export const optionalPasswordField = () =>
  Yup.string()
    .optional()
    .matches(PASSWORD_LENGTH_REGEX, { message: PASSWORD_RANGE_MESSAGE, excludeEmptyString: true });
